import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { lazy, Suspense } from "react";
import { GoogleTagManager } from "./components/GoogleTagManager";


// Critical path - load immediately for fast FCP
import Index from "./pages/Index";

// Lazy load all other pages
const NotFound = lazy(() => import("./pages/NotFound"));
const Diensten = lazy(() => import("./pages/Diensten"));
const OverMij = lazy(() => import("./pages/OverMij"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const PortfolioDetail = lazy(() => import("./pages/PortfolioDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const Offerte = lazy(() => import("./pages/Offerte"));
const Kennismaking = lazy(() => import("./pages/Kennismaking"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Nieuws = lazy(() => import("./pages/Nieuws"));
const NieuwsPost = lazy(() => import("./pages/NieuwsPost"));
const Assistent = lazy(() => import("./pages/Assistent"));
const SeoPage = lazy(() => import("./pages/SeoPage"));
const Werkgebieden = lazy(() => import("./pages/Werkgebieden"));
const Onderwerpen = lazy(() => import("./pages/Onderwerpen"));
const Privacybeleid = lazy(() => import("./pages/Privacybeleid"));
const AlgemeneVoorwaarden = lazy(() => import("./pages/AlgemeneVoorwaarden"));
const AIKansenScan = lazy(() => import("./pages/tools/AIKansenScan"));
const ToolsOverview = lazy(() => import("./pages/tools/Tools"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));



// Admin pages - lazy loaded
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const NewsEditor = lazy(() => import("./pages/admin/NewsEditor"));
const PortfolioEditor = lazy(() => import("./pages/admin/PortfolioEditor"));
const AccountEditor = lazy(() => import("./pages/admin/AccountEditor"));
const ServiceEditor = lazy(() => import("./pages/admin/ServiceEditor"));

const ResendSettings = lazy(() => import("./pages/admin/ResendSettings"));
const LovableAISettings = lazy(() => import("./pages/admin/LovableAISettings"));
const TagManagerSettings = lazy(() => import("./pages/admin/TagManagerSettings"));
const MFASetup = lazy(() => import("./pages/admin/MFASetup"));
const MFAVerify = lazy(() => import("./pages/admin/MFAVerify"));
const AccountProfile = lazy(() => import("./pages/admin/AccountProfile"));

const AssistantConversations = lazy(() => import("./pages/admin/AssistantConversations"));
const SeoPageEditor = lazy(() => import("./pages/admin/SeoPageEditor"));
const StandaloneSeoPageEditor = lazy(() => import("./pages/admin/StandaloneSeoPageEditor"));
const PageSeoEditor = lazy(() => import("./pages/admin/PageSeoEditor"));
const AdminAIKansenScanSubmissions = lazy(() => import("./pages/admin/tools/AIKansenScanSubmissions"));
const AdminAIKansenScanDetail = lazy(() => import("./pages/admin/tools/AIKansenScanDetail"));
const AdminAIKansenScanInstellingen = lazy(() => import("./pages/admin/tools/AIKansenScanInstellingen"));
const ProtectedAdminRoute = lazy(() => import("./components/admin/ProtectedAdminRoute").then(m => ({ default: m.ProtectedAdminRoute })));

// Service pages - lazy loaded
const Domeinnamen = lazy(() => import("./pages/diensten/Domeinnamen"));
const Hosting = lazy(() => import("./pages/diensten/Hosting"));
const WordPressHosting = lazy(() => import("./pages/diensten/WordPressHosting"));
const WebsitesBouwen = lazy(() => import("./pages/diensten/WebsitesBouwen"));
const WebshopsBouwen = lazy(() => import("./pages/diensten/WebshopsBouwen"));
const SEO = lazy(() => import("./pages/diensten/SEO"));
const SEA = lazy(() => import("./pages/diensten/SEA"));
const ZakelijkeEmail = lazy(() => import("./pages/diensten/ZakelijkeEmail"));
const GoogleWorkspace = lazy(() => import("./pages/diensten/GoogleWorkspace"));
const WebsiteOnderhoud = lazy(() => import("./pages/diensten/WebsiteOnderhoud"));
const AIOptimalisatie = lazy(() => import("./pages/diensten/AIOptimalisatie"));
const WebsitePerMaand = lazy(() => import("./pages/diensten/WebsitePerMaand"));

const queryClient = new QueryClient();

// Page loader with OSS beeldmerk
import { BrandMark } from "./components/BrandMark";
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4">
    <BrandMark size={64} variant="loader" ariaLabel="Laden" />
    <span className="text-sm text-muted-foreground tracking-wide">Laden…</span>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <GoogleTagManager />
      
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/diensten" element={<Diensten />} />
            <Route path="/diensten/domeinnamen" element={<Domeinnamen />} />
            <Route path="/diensten/hosting" element={<Hosting />} />
            <Route path="/diensten/wordpress-hosting" element={<WordPressHosting />} />
            <Route path="/diensten/websites-bouwen" element={<WebsitesBouwen />} />
            <Route path="/diensten/webshops-bouwen" element={<WebshopsBouwen />} />
            <Route path="/diensten/seo" element={<SEO />} />
            <Route path="/diensten/sea" element={<SEA />} />
            <Route path="/diensten/zakelijke-email" element={<ZakelijkeEmail />} />
            <Route path="/diensten/google-workspace" element={<GoogleWorkspace />} />
            <Route path="/diensten/website-onderhoud" element={<WebsiteOnderhoud />} />
            <Route path="/diensten/ai-optimalisatie" element={<AIOptimalisatie />} />
            <Route path="/diensten/website-per-maand" element={<WebsitePerMaand />} />
            <Route path="/over-mij" element={<OverMij />} />
            <Route path="/projecten" element={<Portfolio />} />
            <Route path="/projecten/:id" element={<PortfolioDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/offerte-aanvragen" element={<Offerte />} />
            <Route path="/kennismaking" element={<Kennismaking />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/nieuws" element={<Nieuws />} />
            <Route path="/nieuws/:slug" element={<NieuwsPost />} />
            <Route path="/online-sale-specialist-assistent" element={<Assistent />} />
            <Route path="/privacy" element={<Privacybeleid />} />
            <Route path="/voorwaarden" element={<AlgemeneVoorwaarden />} />
            <Route path="/tools" element={<ToolsOverview />} />
            <Route path="/tools/ai-kansen-scan" element={<AIKansenScan />} />

            {/* OAuth consent screen for MCP clients */}
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />


            
            {/* Admin Routes - Public (no auth required) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/mfa-setup" element={<MFASetup />} />
            <Route path="/admin/mfa-verify" element={<MFAVerify />} />
            
            {/* Admin Routes - Protected (auth required) */}
            <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
            <Route path="/admin/blog/:id" element={<ProtectedAdminRoute><BlogEditor /></ProtectedAdminRoute>} />
            <Route path="/admin/nieuws/:id" element={<ProtectedAdminRoute><NewsEditor /></ProtectedAdminRoute>} />
            <Route path="/admin/portfolio/:id" element={<ProtectedAdminRoute><PortfolioEditor /></ProtectedAdminRoute>} />
            <Route path="/admin/accounts/new" element={<ProtectedAdminRoute><AccountEditor /></ProtectedAdminRoute>} />
            <Route path="/admin/accounts/:id" element={<ProtectedAdminRoute><AccountProfile /></ProtectedAdminRoute>} />
            <Route path="/admin/services/:slug" element={<ProtectedAdminRoute><ServiceEditor /></ProtectedAdminRoute>} />
            
            <Route path="/admin/settings/resend" element={<ProtectedAdminRoute><ResendSettings /></ProtectedAdminRoute>} />
            <Route path="/admin/settings/lovable-ai" element={<ProtectedAdminRoute><LovableAISettings /></ProtectedAdminRoute>} />
            <Route path="/admin/settings/tag-manager" element={<ProtectedAdminRoute><TagManagerSettings /></ProtectedAdminRoute>} />
            <Route path="/admin/assistent/gesprekken" element={<ProtectedAdminRoute><AssistantConversations /></ProtectedAdminRoute>} />
            <Route path="/admin/seo-pages/:id" element={<ProtectedAdminRoute><SeoPageEditor /></ProtectedAdminRoute>} />
            <Route path="/admin/seo-paginas/:id" element={<ProtectedAdminRoute><StandaloneSeoPageEditor /></ProtectedAdminRoute>} />
            <Route path="/admin/page-seo/:id" element={<ProtectedAdminRoute><PageSeoEditor /></ProtectedAdminRoute>} />
            <Route path="/admin/tools/ai-kansen-scan" element={<ProtectedAdminRoute><AdminAIKansenScanSubmissions /></ProtectedAdminRoute>} />
            <Route path="/admin/tools/ai-kansen-scan/instellingen" element={<ProtectedAdminRoute><AdminAIKansenScanInstellingen /></ProtectedAdminRoute>} />
            <Route path="/admin/tools/ai-kansen-scan/:id" element={<ProtectedAdminRoute><AdminAIKansenScanDetail /></ProtectedAdminRoute>} />
            
            {/* Overview pages */}
            <Route path="/werkgebieden" element={<Werkgebieden />} />
            <Route path="/onderwerpen" element={<Onderwerpen />} />
            
            {/* 404 explicit route */}
            <Route path="/404" element={<NotFound />} />
            
            {/* SEO Pages - dynamic slug routes (must be before catch-all) */}
            <Route path="/:slug" element={<SeoPage />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
